/** Replacement for US SSN-shaped sequences found in otherwise-visible text. */
export const RYN_REPLAY_MASKED_SSN = '***-**-****';

/** Replacement for the four digits after an explicit SSN / last-4 label. */
const MASKED_LAST4 = '****';

// No lookbehind anywhere: this module is shared with NativeScript, whose iOS JavaScriptCore lacks it before iOS 16.4.

/** Every masked shape contains four consecutive digits (non-global, so `test` keeps no lastIndex state). */
const FOUR_DIGITS = /\d{4}/;

/**
 * Full SSN, separated by hyphen, space, dot, or an en/em dash from pasted text (PR 5389 review):
 * 123-45-6789, 987 65 4321, 123.45.6789, 123–45–6789.
 */
const FULL_SSN = /\b\d{3}[-\s.\u2013\u2014]\d{2}[-\s.\u2013\u2014]\d{4}\b/g;

/**
 * Nine unbroken digits (an unformatted SSN). Anchored on a non-digit rather than \b, because an underscore is a word
 * character and \b missed 'ssn_123456789' (PR 5389 review). Group 1 keeps that character. Ten-digit phones and
 * eight-digit numbers do not match.
 */
const NINE_DIGITS = /(^|\D)\d{9}(?!\d)/g;

/**
 * A partially masked SSN that still shows digits: XXX-45-6789, XXX-XX-1234 (RA's 'XXX-XX-' + Last4SSN), ***-**-1234,
 * and with no separators at all, xxx456789 (the Viewpoint employees grid's 'xxx' + SSN.Substring(3), PR 5389 review).
 * Group 1 keeps the character before it (start of text or a non-word, non-'*' character), standing in for a lookbehind.
 */
const PARTIAL_SSN =
  /(^|[^\w*])[Xx*]{3}[-\s.\u2013\u2014]?[Xx*\d]{2}[-\s.\u2013\u2014]?\d{4}\b/g;

/**
 * Four digits after an explicit SSN / last-4 label ("Last 4 SSN: 1234", "SSN #1234", "Social Security Number: 1234").
 * A bare four-digit number is never masked: quantities, years and the "Last, First (1234)" display-name suffix that
 * timekeepers use to tell same-name employees apart stay readable (bug 35675 scope; render sites are PR B).
 */
const LABELLED_LAST4 =
  /\b(SSN|SS#|Social Security(?: Number| No\.?)?|Last\s?(?:4|Four)(?: of)?(?: SSN)?)(\s*[:#]?\s*)\d{4}\b/gi;

/**
 * Mask US Social-Security-shaped text: full SSNs (any common separator or none), partially masked forms that still
 * expose digits, and a last-4 next to an explicit SSN label. Shared by the console scrub, the NativeScript view-tree
 * mask and (bug 35675) every web rrweb event, so all three mask the same shapes.
 *
 * Fast path: every shape above contains four consecutive digits, so text without them (most of a page) skips the four
 * regex passes; the web scrub runs this on every text node and attribute of every snapshot.
 */
export const maskSsnLikeInText = (text: string): string =>
  !FOUR_DIGITS.test(text)
    ? text
    : text
        .replace(FULL_SSN, RYN_REPLAY_MASKED_SSN)
        .replace(NINE_DIGITS, `$1${RYN_REPLAY_MASKED_SSN}`)
        .replace(PARTIAL_SSN, `$1${RYN_REPLAY_MASKED_SSN}`)
        .replace(LABELLED_LAST4, `$1$2${MASKED_LAST4}`);
